import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import './ProjectImpactOverviewBar.scss';

const ProjectImpactOverviewBar = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('project-impact-bar-section');
      if (element) {
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Check if section is 50% visible
        if (rect.top < windowHeight * 0.5 && rect.bottom > windowHeight * 0.5) {
          setIsVisible(true);
        } else if (rect.bottom < -100 || rect.top > windowHeight + 100) {
          // Reset when section is completely out of view
          setIsVisible(false);
        }
      }
    };

    // Check on mount to trigger animation immediately if section is visible
    // Add small delay to ensure DOM is fully rendered
    setTimeout(() => {
      handleScroll();
    }, 100);
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const chartOptions = {
    chart: {
      type: 'column',
      height: window.innerWidth <= 480 ? 250 : window.innerWidth <= 768 ? 300 : window.innerWidth <= 1024 ? 350 : 420,
      backgroundColor: 'transparent',
      spacing: [20, 20, 20, 20],
      responsive: {
        rules: [{
          condition: {
            maxWidth: 480
          },
          chartOptions: {
            height: 250,
            spacing: [10, 10, 10, 10]
          }
        }, {
          condition: {
            maxWidth: 768
          },
          chartOptions: {
            height: 300,
            spacing: [15, 15, 15, 15]
          }
        }, {
          condition: {
            maxWidth: 1024
          },
          chartOptions: {
            height: 350,
            spacing: [15, 15, 15, 15]
          }
        }]
      }
    },
    title: {
      text: null
    },
    xAxis: {
      categories: ['Household Light', 'Productive Use', 'Community Services', 'Cooking Solutions', 'Industrial Use'],
      labels: {
        style: {
          fontSize: window.innerWidth <= 480 ? '10px' : window.innerWidth <= 768 ? '11px' : '12px',
          color: '#333'
        },
        rotation: window.innerWidth <= 480 ? -60 : window.innerWidth <= 768 ? -45 : -45
      }
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Percentage (%)',
        style: {
          fontSize: window.innerWidth <= 480 ? '11px' : window.innerWidth <= 768 ? '12px' : '14px',
          color: '#333'
        }
      },
      labels: {
        style: {
          fontSize: window.innerWidth <= 480 ? '10px' : window.innerWidth <= 768 ? '11px' : '12px',
          color: '#333'
        }
      },
      gridLineColor: '#e0e0e'
    },
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0,
        borderRadius: 4,
        dataLabels: {
          enabled: true,
          format: '{y}%',
          style: {
            fontSize: window.innerWidth <= 480 ? '9px' : window.innerWidth <= 768 ? '10px' : '12px',
            fontWeight: 'bold',
            color: 'white',
            textOutline: 'none'
          }
        }
      }
    },
    series: [{
      name: 'Impact Distribution',
      colorByPoint: true,
      data: [
        {
          name: 'Household Light',
          y: 35,
          color: '#3d9b35'
        },
        {
          name: 'Productive Use',
          y: 25,
          color: '#d4c62f'
        },
        {
          name: 'Community Services',
          y: 15,
          color: '#f5b932'
        },
        {
          name: 'Cooking Solutions',
          y: 15,
          color: '#3ac7e0'
        },
        {
          name: 'Industrial Use',
          y: 10,
          color: '#f3b494'
        }
      ]
    }],
    legend: {
      enabled: false
    },
    credits: {
      enabled: false
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'transparent',
      borderRadius: 8,
      style: {
        color: 'white',
        fontSize: window.innerWidth <= 480 ? '10px' : window.innerWidth <= 768 ? '11px' : '12px'
      },
      formatter: function() {
        return `<b>${this.point.name}</b><br/>${this.y}% of total impact`;
      }
    }
  };

  return (
    <section id="project-impact-bar-section" className={`project-impact-bar-section ${isVisible ? 'animate' : ''}`}>
      <div className="project-impact-content">
        {/* Left Column - Text and Stats */}
        <div className="project-impact-left">
          <h2 className="section-title">Project Impact Overview</h2>
          <p className="section-subtitle">
            Understanding how energy investments are changing lives across Africa
          </p>
          
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Household Electrified</div>
              <div className="stat-number">4.5M</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">CO₂ Reduction</div>
              <div className="stat-number">3.2M tons</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Jobs created</div>
              <div className="stat-number">125.0K</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Countries impacted</div>
              <div className="stat-number">28</div>
            </div>
          </div>
        </div>

        {/* Right Column - Bar Chart */}
        <div className="project-impact-right">
          <div className="chart-container">
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectImpactOverviewBar;
